import { type SubmitEvent, useEffect, useState } from 'react'
import {
  type ApiCbrRate,
  type ApiTeamRate,
  fetchTeamRate,
  updateTeamRate,
} from '@/fetcher'
import { SectionCard } from './SectionCard'
import { StatusToast } from './StatusToast'

type TeamRateFormProps = {
  teamId: string
  cbrRate: ApiCbrRate | null
}

const formatRateInput = (value: string) => {
  const normalized = value.replace(',', '.')
  return /^\d+(?:[.,]\d+)?$/.test(normalized) ? normalized : ''
}

const toInputValue = (numeric: string) => {
  const value = Number(numeric)
  return Number.isFinite(value) ? String(value) : ''
}

const formatRub = (value: number) => Number(value.toFixed(4)).toString()

export const TeamRateForm = ({ teamId, cbrRate }: TeamRateFormProps) => {
  const [team, setTeam] = useState<ApiTeamRate | null>(null)
  const [autoUpdateRate, setAutoUpdateRate] = useState(false)
  const [usdtSellRate, setUsdtSellRate] = useState('')
  const [usdtRateMarkupPercent, setUsdtRateMarkupPercent] = useState('')
  const [usdtRateThresholdPercent, setUsdtRateThresholdPercent] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [toast, setToast] = useState<{
    message: string
    tone: 'error' | 'success'
  } | null>(null)

  useEffect(() => {
    let isMounted = true

    setIsLoading(true)
    setTeam(null)
    fetchTeamRate(teamId)
      .then((payload) => {
        if (!isMounted) return
        setTeam(payload)
        setAutoUpdateRate(payload.autoUpdateRate)
        setUsdtSellRate(toInputValue(payload.usdtSellRate))
        setUsdtRateMarkupPercent(toInputValue(payload.usdtRateMarkupPercent))
        setUsdtRateThresholdPercent(
          payload.usdtRateThresholdPercent == null
            ? ''
            : toInputValue(payload.usdtRateThresholdPercent),
        )
      })
      .catch((error) => {
        if (!isMounted) return
        setToast({
          message:
            error instanceof Error
              ? error.message
              : 'Не удалось загрузить курс компании',
          tone: 'error',
        })
      })
      .finally(() => {
        if (isMounted) setIsLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [teamId])

  const thresholdInputValue = formatRateInput(usdtRateThresholdPercent)
  const thresholdPercent =
    thresholdInputValue === '' ? null : Number(thresholdInputValue)
  const thresholdRub =
    cbrRate?.rate != null &&
    thresholdPercent != null &&
    Number.isFinite(thresholdPercent)
      ? cbrRate.rate * (1 + thresholdPercent / 100)
      : null

  const handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault()

    const rateValue = formatRateInput(usdtSellRate)
    const markupValue = formatRateInput(usdtRateMarkupPercent)

    if (!rateValue || !markupValue) {
      setToast({ message: 'Заполните все обязательные поля', tone: 'error' })
      return
    }

    const thresholdValue = formatRateInput(usdtRateThresholdPercent)
    const thresholdPayload =
      thresholdValue === '' ? null : Number(thresholdValue)

    if (thresholdPayload != null && thresholdPayload < 0) {
      setToast({ message: 'Порог не может быть отрицательным', tone: 'error' })
      return
    }

    setIsSaving(true)
    try {
      const updated = await updateTeamRate(teamId, {
        usdtSellRate: Number(rateValue),
        usdtRateMarkupPercent: Number(markupValue),
        usdtRateThresholdPercent: thresholdPayload,
        autoUpdateRate,
      })
      setTeam(updated)
      setUsdtSellRate(toInputValue(updated.usdtSellRate))
      setUsdtRateMarkupPercent(toInputValue(updated.usdtRateMarkupPercent))
      setUsdtRateThresholdPercent(
        updated.usdtRateThresholdPercent == null
          ? ''
          : toInputValue(updated.usdtRateThresholdPercent),
      )
      setToast({ message: 'Курс сохранён', tone: 'success' })
    } catch (error) {
      setToast({
        message:
          error instanceof Error ? error.message : 'Не удалось сохранить курс',
        tone: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="rounded-[22px] bg-[var(--app-card)] px-4 py-6 text-center text-sm text-[var(--app-muted)]">
        Загрузка курса...
      </div>
    )
  }

  if (!team) {
    return null
  }

  return (
    <>
      <form className="space-y-4" onSubmit={handleSubmit}>
        <SectionCard title="Режим курса">
          <div className="flex gap-2">
            <button
              type="button"
              className={`flex-1 rounded-[18px] px-4 py-3 text-sm font-medium transition ${
                autoUpdateRate
                  ? 'bg-[var(--app-accent)] text-[var(--app-accent-text)]'
                  : 'bg-[var(--app-input)] text-[var(--app-muted)]'
              }`}
              onClick={() => setAutoUpdateRate(true)}
            >
              Плавающий
            </button>
            <button
              type="button"
              className={`flex-1 rounded-[18px] px-4 py-3 text-sm font-medium transition ${
                !autoUpdateRate
                  ? 'bg-[var(--app-accent)] text-[var(--app-accent-text)]'
                  : 'bg-[var(--app-input)] text-[var(--app-muted)]'
              }`}
              onClick={() => setAutoUpdateRate(false)}
            >
              Фиксированный
            </button>
          </div>
        </SectionCard>

        <SectionCard
          title="Параметры курса"
          footer={
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex min-h-12 w-full items-center justify-center rounded-full bg-[var(--app-accent)] px-5 py-3 text-sm font-semibold text-[var(--app-accent-text)] transition hover:opacity-90 disabled:opacity-50"
            >
              {isSaving ? 'Сохранение...' : 'Сохранить'}
            </button>
          }
        >
          <div className="space-y-3">
            <div className="space-y-2">
              <label
                className="block text-sm font-medium text-[var(--app-text)]"
                htmlFor="usdt-rate-markup-percent"
              >
                Коэффициент (%)
              </label>
              <input
                id="usdt-rate-markup-percent"
                className="w-full rounded-[14px] bg-[var(--app-input)] px-4 py-3 text-sm text-[var(--app-text)] outline-none focus:ring-2 focus:ring-[var(--app-accent-soft)]"
                inputMode="decimal"
                placeholder="0"
                value={usdtRateMarkupPercent}
                onChange={(event) =>
                  setUsdtRateMarkupPercent(event.target.value)
                }
              />
            </div>

            <div className="space-y-2">
              <label
                className="block text-sm font-medium text-[var(--app-text)]"
                htmlFor="usdt-sell-rate"
              >
                Значение (RUB)
              </label>
              <input
                id="usdt-sell-rate"
                className="w-full rounded-[14px] bg-[var(--app-input)] px-4 py-3 text-sm text-[var(--app-text)] outline-none focus:ring-2 focus:ring-[var(--app-accent-soft)]"
                inputMode="decimal"
                placeholder="0"
                value={usdtSellRate}
                onChange={(event) => setUsdtSellRate(event.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label
                className="block text-sm font-medium text-[var(--app-text)]"
                htmlFor="usdt-rate-threshold-percent"
              >
                Минимальный порог (%)
              </label>
              <input
                id="usdt-rate-threshold-percent"
                className="w-full rounded-[14px] bg-[var(--app-input)] px-4 py-3 text-sm text-[var(--app-text)] outline-none focus:ring-2 focus:ring-[var(--app-accent-soft)]"
                inputMode="decimal"
                placeholder="Не задан"
                value={usdtRateThresholdPercent}
                onChange={(event) =>
                  setUsdtRateThresholdPercent(event.target.value)
                }
              />
              <p className="text-xs text-[var(--app-muted)]">
                Минимальный курс:{' '}
                {thresholdRub != null
                  ? `${formatRub(thresholdRub)} ₽`
                  : cbrRate == null
                    ? 'курс ЦБ недоступен'
                    : 'не задан'}
              </p>
            </div>
          </div>
        </SectionCard>
      </form>

      {toast && (
        <StatusToast
          message={toast.message}
          tone={toast.tone}
          onClose={() => setToast(null)}
        />
      )}
    </>
  )
}
